import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Mic2, Trash2, Clock, CheckCircle2, Loader2, AlertCircle, Film } from 'lucide-react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';

const STATUS = {
  draft: { label: 'Draft', cls: 'text-[#8A8A93] bg-white/[0.04] border-white/[0.08]', Icon: Clock },
  rendering: { label: 'Rendering', cls: 'text-[#E879F9] bg-[#E879F9]/10 border-[#E879F9]/25', Icon: Loader2 },
  complete: { label: 'Ready', cls: 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/25', Icon: CheckCircle2 },
  failed: { label: 'Failed', cls: 'text-red-400 bg-red-500/10 border-red-500/25', Icon: AlertCircle },
};

export default function Presenter() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get('/presenter').then(r => setVideos(r.data.videos)).catch(() => setVideos([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const res = await api.post('/presenter', { title: 'Untitled presenter video' });
      navigate(`/presenter/${res.data.id}`);
    } catch { toast.error('Could not create presenter video'); setCreating(false); }
  };

  const remove = async (e, v) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try { await api.delete(`/presenter/${v.id}`); setVideos(list => list.filter(x => x.id !== v.id)); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="min-h-screen px-4 sm:px-8 py-8 max-w-7xl mx-auto" data-testid="presenter-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#E879F9] mb-2 flex items-center gap-2"><Mic2 className="w-3.5 h-3.5" /> Presenter mode</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">Talking-head videos</h1>
          <p className="text-sm text-[#8A8A93] mt-2 max-w-xl">One avatar, one voice, up to five minutes of script — rendered by Kling Avatar. Real-looking, anime or pixel art.</p>
        </div>
        <button data-testid="new-presenter-btn" onClick={create} disabled={creating} className="ff-btn-primary text-sm flex items-center gap-2 self-start sm:self-auto disabled:opacity-60">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} New presenter video
        </button>
      </div>

      {videos === null ? (
        <div className="text-[#8A8A93] text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : videos.length === 0 ? (
        <button data-testid="presenter-empty-create" onClick={create} className="w-full ff-card rounded-2xl p-12 text-center border-dashed border-2 border-white/[0.08] hover:border-[#E879F9]/40 transition-colors">
          <Mic2 className="w-10 h-10 text-[#E879F9] mx-auto mb-3" />
          <p className="text-white font-semibold">Create your first presenter video</p>
          <p className="text-sm text-[#8A8A93] mt-1">Paste a script, pick or upload a face, choose a voice, render.</p>
        </button>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map(v => {
            const s = STATUS[v.status] || STATUS.draft;
            return (
              <button key={v.id} data-testid={`presenter-card-${v.id}`} onClick={() => navigate(`/presenter/${v.id}`)}
                className="ff-card rounded-2xl overflow-hidden text-left group hover:-translate-y-0.5 transition-transform">
                <div className={`${v.aspect_ratio === '9:16' ? 'aspect-video' : 'aspect-video'} bg-[#121216] relative`}>
                  {v.avatar?.image_path ? (
                    <img src={getImageUrl(v.avatar.image_path)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#55555C]"><Film className="w-8 h-8" /></div>
                  )}
                  <span className={`absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${s.cls}`}>
                    <s.Icon className={`w-3 h-3 ${v.status === 'rendering' ? 'animate-spin' : ''}`} /> {s.label}
                  </span>
                  <button data-testid={`presenter-delete-${v.id}`} onClick={(e) => remove(e, v)} title="Delete"
                    className="absolute top-2 right-2 w-7 h-7 rounded-lg bg-black/50 text-[#C9C9CF] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold text-white truncate">{v.title}</p>
                  <p className="text-[11px] text-[#8A8A93] mt-0.5 truncate">
                    {v.audio_duration ? `${Math.round(v.audio_duration)}s · ` : ''}{v.voice?.voice_name || (v.voice?.provider === 'upload' ? 'Uploaded audio' : 'No voice yet')}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
